import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import MotivareMobile, { useMotivareMe } from '@/components/driver/MotivareMobile';
import { ReportProblemButton } from '@/components/ReportProblemButton';
import { LogOut, PlayCircle, StopCircle, Car, Activity, Fuel, AlertTriangle, FileText, Award, CalendarDays, ChevronLeft, ChevronRight, Camera } from 'lucide-react';

const MENU_ITEMS = [
  { id: 'foaie', label: 'Foaie de Parcurs Auto', icon: Car, color: 'from-blue-600 to-blue-700', iconBg: 'bg-blue-500' },
  { id: 'monitorizare', label: 'Monitorizare Diferențe', icon: AlertTriangle, color: 'from-orange-500 to-orange-600', iconBg: 'bg-orange-400' },
  { id: 'motivare', label: '', icon: Award, color: 'from-amber-500 to-amber-600', iconBg: 'bg-amber-400' },
  { id: 'cerere_co', label: '', icon: CalendarDays, color: 'from-emerald-600 to-emerald-700', iconBg: 'bg-emerald-500' },
];

export default function DriverDashboard({ auth }) {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(null);
  const [hasActiveTrip, setHasActiveTrip] = useState(false);
  const [incidentsCount, setIncidentsCount] = useState(0);
  const { me: motivareMe, isNew: motivareNew } = useMotivareMe(auth, activeSection);
  const motivareVisible = !!motivareMe?.visible;

  useEffect(() => {
    if (!auth?.token || activeSection !== null) return;
    // Check active trip
    axios.get(`${API}/trips/my-active`, { headers: { Authorization: `Bearer ${auth.token}` } })
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
        setHasActiveTrip(list.length > 0 && !!list[0]?.id);
      }).catch(() => {});
    // Check incidents
    axios.get(`${API}/incidents?employee_id=${auth.user?.id}`, { headers: { Authorization: `Bearer ${auth.token}` } })
      .then(res => {
        const data = res.data;
        if (data?.employees && Array.isArray(data.employees)) {
          const my = data.employees.find(e => e.employee_id === auth.user?.id);
          setIncidentsCount(my?.incidents?.length || 0);
        }
      }).catch(() => {});
  }, [auth?.token, auth?.user?.id, activeSection]);

  if (!auth || !auth.token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <div className="text-lg mb-4">Se încarcă...</div>
          <Button onClick={() => navigate('/login')} variant="outline">Către pagina de autentificare</Button>
        </div>
      </div>
    );
  }

  const goBack = () => setActiveSection(null);

  // Section content
  if (activeSection) {
    const current = { ...MENU_ITEMS.find(m => m.id === activeSection) };
    if (current.id === 'motivare' && motivareVisible) current.label = 'Motivare';
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Section header with back button */}
        <div className={`bg-gradient-to-r ${current.color} text-white px-4 py-3 flex items-center gap-3 sticky top-0 z-40 shadow-md`}>
          <button onClick={goBack} className="p-2.5 rounded-lg bg-white/20 active:bg-white/30 min-w-[44px] min-h-[44px] flex items-center justify-center" data-testid="back-button" aria-label="Înapoi la meniu">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <current.icon className="w-5 h-5 shrink-0" />
            <span className="font-semibold text-base truncate">{current.label}</span>
          </div>
        </div>

        {/* Section body */}
        <div className="p-3 pb-20">
          {activeSection === 'foaie' && <FoaieParcursTab auth={auth} />}
          {activeSection === 'monitorizare' && <MonitorizareTab auth={auth} />}
          {activeSection === 'motivare' && (motivareVisible ? <MotivareMobile auth={auth} /> : <PlaceholderTab />)}
          {activeSection === 'cerere_co' && <PlaceholderTab />}
        </div>

        {/* Logout fixed bottom */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-3 z-50">
          <Button variant="outline" size="sm" onClick={() => { localStorage.removeItem('token'); navigate('/login'); }}
            className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300" data-testid="logout-button">
            <LogOut className="w-4 h-4 mr-2" />Deconectare
          </Button>
        </div>
      </div>
    );
  }

  // Main menu — 4 buttons stacked vertically
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b px-4 py-4">
        <h1 className="text-xl font-bold text-slate-800" data-testid="driver-welcome">
          Bun venit, {auth.user?.username}
        </h1>
        {auth.user?.roles && auth.user.roles.length > 1 && (
          <select
            value={auth.user?.role || ''}
            onChange={async (e) => {
              if (auth.switchRole) {
                const result = await auth.switchRole(e.target.value);
                if (result?.success) window.location.href = '/admin';
              }
            }}
            className="mt-2 w-full text-sm border border-slate-300 rounded-md px-3 py-2 bg-white"
            data-testid="role-switcher"
          >
            {auth.user?.roles?.map(role => (
              <option key={role} value={role}>
                {role === 'admin' ? 'Administrator' : 
                 role === 'fleet_manager' ? 'Manager Flotă' :
                 role === 'contabilitate' ? 'Contabilitate' :
                 role === 'juridic' ? 'Juridic' :
                 role === 'dispatcher' ? 'Dispecer' :
                 role === 'tesa' ? 'TESA' :
                 role === 'driver' ? 'Șofer' : role}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Menu Buttons */}
      <div className="p-4 space-y-3 pb-24" data-testid="driver-menu">
        {MENU_ITEMS.map(item => (
          <button
            key={item.id}
            onClick={() => setActiveSection(item.id)}
            data-testid={`menu-btn-${item.id}`}
            className={`w-full bg-gradient-to-r ${item.color} text-white rounded-xl p-4 flex items-center gap-4 active:scale-[0.98] transition-transform shadow-sm`}
          >
            <div className={`${item.iconBg} p-3 rounded-lg`}>
              <item.icon className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 text-left">
              <span className="text-base font-semibold block">{item.id === 'motivare' && motivareVisible ? 'Motivare' : item.label}</span>
              {item.id === 'motivare' && motivareVisible && motivareMe?.activity_period && (
                <span className="text-xs text-white/80 block mt-0.5 flex items-center gap-1.5" data-testid="motivare-hint">
                  Situația la {motivareMe.activity_period.end.slice(8, 10)}.{motivareMe.activity_period.end.slice(5, 7)}
                  {motivareNew && <span className="inline-flex items-center gap-1 bg-white/25 rounded-full px-1.5 py-px text-[10px] font-semibold" data-testid="motivare-new-badge"><span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> raportare nouă</span>}
                </span>
              )}
              {item.id === 'foaie' && hasActiveTrip && (
                <span className="text-xs text-white/80 block mt-0.5" data-testid="active-trip-hint">Ai o cursă activă</span>
              )}
              {item.id === 'monitorizare' && incidentsCount > 0 && (
                <span className="text-xs text-white/80 block mt-0.5" data-testid="incidents-hint">{incidentsCount} expediții neclarificate</span>
              )}
            </div>
            <ChevronRight className="w-5 h-5 text-white/70" />
          </button>
        ))}
        {(auth.user?.permissions || []).includes('facturi.foto') && (
          <button onClick={() => navigate('/facturi-foto')} data-testid="menu-btn-facturi-foto"
            className="w-full bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-xl p-4 flex items-center gap-4 active:scale-[0.98] transition-transform shadow-sm">
            <div className="bg-slate-600 p-3 rounded-lg"><Camera className="w-6 h-6 text-white" /></div>
            <div className="flex-1 text-left"><span className="text-base font-semibold block">Foto documente</span><span className="text-xs text-white/80 block mt-0.5">Fotografiază facturi, documente și chitanțe</span></div>
            <ChevronRight className="w-5 h-5 text-white/70" />
          </button>
        )}
      </div>

      {/* Logout fixed bottom */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-3 z-50 space-y-2">
        <ReportProblemButton auth={auth} section="meniu-sofer" variant="ghost" className="w-full text-slate-500 hover:text-slate-700" label="Raportează o problemă" />
        <Button variant="outline" size="sm" onClick={() => { localStorage.removeItem('token'); navigate('/login'); }}
          className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300" data-testid="logout-button">
          <LogOut className="w-4 h-4 mr-2" />Deconectare
        </Button>
      </div>
    </div>
  );
}

// ============ Tab 2: Monitorizare Diferențe (read-only) ============
function MonitorizareTab({ auth }) {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIncidents();
  }, []);

  const fetchIncidents = async () => {
    try {
      const res = await axios.get(`${API}/incidents?employee_id=${auth.user?.id}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      const data = res.data;
      // API returns {employees: [{incidents: [...]}], month, year}
      if (data?.employees && Array.isArray(data.employees)) {
        const myEmployee = data.employees.find(e => e.employee_id === auth.user?.id);
        setIncidents(myEmployee?.incidents || []);
      } else if (Array.isArray(data)) {
        setIncidents(data);
      } else {
        setIncidents([]);
      }
    } catch {
      setIncidents([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-5">
        {loading ? (
          <p className="text-center text-slate-500 py-8">Se încarcă...</p>
        ) : incidents.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-sm">Nicio diferență înregistrată</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>AWB</TableHead>
                <TableHead>Tip</TableHead>
                <TableHead className="text-right">Valoare</TableHead>
                <TableHead>Descriere</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {incidents.map((inc, idx) => (
                <TableRow key={inc.id || idx}>
                  <TableCell className="text-xs whitespace-nowrap">{inc.data_incident || inc.data || inc.date || '-'}</TableCell>
                  <TableCell className="text-xs">{inc.awb || '-'}</TableCell>
                  <TableCell className="text-xs">{inc.tip_incident || '-'}</TableCell>
                  <TableCell className="text-xs text-right font-semibold">{(inc.valoare ?? inc.suma) != null ? Number(inc.valoare ?? inc.suma).toLocaleString('ro-RO') : '-'}</TableCell>
                  <TableCell className="text-xs">{inc.descriere || inc.description || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ============ Placeholder (butoane fără funcționalitate) ============
function PlaceholderTab() {
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="text-center py-16 text-slate-400">
          <p className="text-sm">În dezvoltare</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ============ Tab 1: Foaie de Parcurs Auto (existing functionality) ============
function FoaieParcursTab({ auth }) {
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [carsError, setCarsError] = useState(null);
  const [activeTrip, setActiveTrip] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [fixedDeparture, setFixedDeparture] = useState('Slobozia - depozit');
  const [routes, setRoutes] = useState([]);
  const [closingReasons, setClosingReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFuelDialog, setShowFuelDialog] = useState(false);
  const [showAddFuelDialog, setShowAddFuelDialog] = useState(false);
  const [fuelLevelRequired, setFuelLevelRequired] = useState(null);
  const [selectedFuelLevel, setSelectedFuelLevel] = useState('');
  
  const [newFuelLiters, setNewFuelLiters] = useState('');
  const [newFuelCost, setNewFuelCost] = useState('');
  const [newFuelKm, setNewFuelKm] = useState('');
  const [newFuelNote, setNewFuelNote] = useState('');
  const [newFuelType, setNewFuelType] = useState('motorina');
  
  const [selectedCar, setSelectedCar] = useState('');
  const [selectedRoute, setSelectedRoute] = useState('');
  const [carSearchFilter, setCarSearchFilter] = useState('');
  const [routeSearchFilter, setRouteSearchFilter] = useState('');
  const [startKm, setStartKm] = useState('');
  
  const [endKm, setEndKm] = useState('');
  const [closingReason, setClosingReason] = useState('');
  const [notes, setNotes] = useState('');
  
  const [carDocuments, setCarDocuments] = useState(null);
  const [showDocuments, setShowDocuments] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${auth?.token}` },
  });

  useEffect(() => {
    if (!auth?.token) return;
    let isMounted = true;
    const loadData = async () => {
      if (isMounted) {
        await fetchActiveTrip();
        await fetchCars();
        await fetchRoutes();
        await fetchClosingReasons();
        if (isMounted) setInitialLoading(false);
      }
    };
    loadData();
    return () => { isMounted = false; };
  }, [auth?.token]);

  const fetchCars = async () => {
    try {
      const res = await axios.get(`${API}/cars`, getAuthHeaders());
      setCars(res.data || []);
      setCarsError(null);
    } catch (err) {
      const status = err?.response?.status;
      setCars([]);
      if (status === 401) {
        setCarsError('auth');
      } else {
        setCarsError('load');
      }
    }
  };

  const fetchActiveTrip = async () => {
    try {
      const res = await axios.get(`${API}/trips/my-active`, getAuthHeaders());
      const list = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
      const trip = list[0] || null;
      setActiveTrip(trip);
      if (trip?.car_id) fetchCarDocuments(trip.car_id);
    } catch { setActiveTrip(null); }
  };

  const fetchRoutes = async () => {
    try {
      const res = await axios.get(`${API}/routes`, getAuthHeaders());
      setRoutes(res.data?.routes || res.data || []);
      if (res.data?.fixed_departure) setFixedDeparture(res.data.fixed_departure);
    } catch { /* silent */ }
  };

  const fetchClosingReasons = async () => {
    try {
      const res = await axios.get(`${API}/closing-reasons?role=driver`, getAuthHeaders());
      setClosingReasons(res.data?.reasons || res.data || []);
    } catch { /* silent */ }
  };

  const fetchCarDocuments = async (carId) => {
    try {
      const res = await axios.get(`${API}/cars/${carId}/documents-status`, getAuthHeaders());
      setCarDocuments(res.data);
    } catch { /* silent */ }
  };

  const checkFuelLevel = async (carId) => {
    try {
      const now = new Date();
      const month = now.getMonth() + 1;
      const year = now.getFullYear();

      // Check if car already has trips this month — if yes, moment has passed, don't ask
      const tripsRes = await axios.get(`${API}/cars/${carId}/trip-sheets?month=${month}&year=${year}`, getAuthHeaders());
      const hasTripsThisMonth = tripsRes.data?.trips && tripsRes.data.trips.length > 0;
      if (hasTripsThisMonth) return true; // Not the first trip, skip

      // First trip of the month — check if fuel level already set
      const res = await axios.get(`${API}/monthly-fuel-levels/${carId}?month=${month}&year=${year}`, getAuthHeaders());
      const hasFuelLevel = res.data?.fuel_level && res.data.fuel_level.start_level != null;
      if (hasFuelLevel) return true; // Already set

      // First trip + no fuel level → ask
      setFuelLevelRequired({ carId, month, year });
      setShowFuelDialog(true);
      return false;
    } catch {
      return true; // Continue if check fails
    }
  };

  const handleSaveFuelLevel = async () => {
    if (!selectedFuelLevel || !fuelLevelRequired) return;
    try {
      const { carId, month, year } = fuelLevelRequired;
      const selectedCarData = cars.find(c => c.id === carId);
      if (!selectedCarData?.fuel_capacity) {
        toast.error('Mașina nu are capacitatea rezervorului setată. Contactează administratorul.');
        return;
      }
      const percentage = parseFloat(selectedFuelLevel);
      const liters = Math.round((selectedCarData.fuel_capacity * percentage) / 100);

      // Save START level for current month
      await axios.post(`${API}/monthly-fuel-levels`, {
        car_id: carId, month, year, level: liters, level_type: 'start'
      }, getAuthHeaders());

      // Save END level for previous month (same value)
      let prevMonth = month - 1;
      let prevYear = year;
      if (prevMonth === 0) { prevMonth = 12; prevYear = year - 1; }
      await axios.post(`${API}/monthly-fuel-levels`, {
        car_id: carId, month: prevMonth, year: prevYear, level: liters, level_type: 'end'
      }, getAuthHeaders());

      const monthNames = ['Ian', 'Feb', 'Mar', 'Apr', 'Mai', 'Iun', 'Iul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      toast.success(`Nivel combustibil salvat! (Sfârșit ${monthNames[prevMonth-1]} și Început ${monthNames[month-1]})`);
      setShowFuelDialog(false);
      setFuelLevelRequired(null);
      setSelectedFuelLevel('');
      return true;
    } catch {
      toast.error('Nu s-a putut salva nivelul de combustibil');
      return false;
    }
  };

  const openTrip = async () => {
    if (!selectedCar || !selectedRoute || !startKm) {
      toast.error('Completează toate câmpurile');
      return;
    }
    // Check fuel level first
    const canProceed = await checkFuelLevel(selectedCar);
    if (!canProceed) return;

    setLoading(true);
    try {
      await axios.post(`${API}/trips/open`, {
        car_id: selectedCar,
        route: selectedRoute,
        start_km: Number(startKm),
        departure_location: fixedDeparture || undefined
      }, getAuthHeaders());
      toast.success('Cursă deschisă cu succes!');
      setSelectedCar(''); setSelectedRoute(''); setStartKm('');
      await fetchActiveTrip();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la deschiderea cursei');
    } finally { setLoading(false); }
  };

  const closeTrip = async (skipFuelCheck = false) => {
    if (!activeTrip || !endKm) {
      toast.error('Completează KM final'); return;
    }
    setLoading(true);
    try {
      const res = await axios.put(`${API}/trips/${activeTrip.id}/close`, {
        end_km: Number(endKm),
        notes,
        closing_reason: closingReason || undefined
      }, getAuthHeaders());
      if (!skipFuelCheck && res.data?.fuel_level_required) {
        setFuelLevelRequired(res.data.fuel_level_required);
        setShowFuelDialog(true);
        setLoading(false);
        return;
      }
      toast.success('Cursă închisă cu succes!');
      setActiveTrip(null); setEndKm(''); setNotes(''); setClosingReason('');
      setCarDocuments(null); setShowDocuments(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la închiderea cursei');
    } finally { setLoading(false); }
  };

  const addFuelEntry = async () => {
    if (!activeTrip || !newFuelLiters || !newFuelCost) {
      toast.error('Completează litrii și costul'); return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/trips/${activeTrip.id}/fuel-entry`, {
        liters: Number(newFuelLiters),
        cost: Number(newFuelCost),
        km: newFuelKm ? Number(newFuelKm) : undefined,
        note: newFuelNote || undefined,
        fuel_type: newFuelType
      }, getAuthHeaders());
      toast.success('Alimentare adăugată!');
      setShowAddFuelDialog(false);
      setNewFuelLiters(''); setNewFuelCost(''); setNewFuelKm(''); setNewFuelNote(''); setNewFuelType('motorina');
      await fetchActiveTrip();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare');
    } finally { setLoading(false); }
  };

  const viewDocument = (doc) => {
    if (!doc.has_document || !doc.document) { toast.info('Nu există document încărcat'); return; }
    try {
      const dataUrl = doc.document;
      // Document is a data URL: data:mime;base64,content
      if (dataUrl.startsWith('data:')) {
        const [header, b64] = dataUrl.split(',');
        const contentType = header.split(':')[1]?.split(';')[0] || 'application/octet-stream';
        const byteChars = atob(b64);
        const byteArray = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteArray], { type: contentType });
        const url = URL.createObjectURL(blob);
        setViewingDocument({ ...doc, url, contentType });
      } else {
        // Fallback: treat as direct URL
        setViewingDocument({ ...doc, url: dataUrl, contentType: 'application/octet-stream' });
      }
    } catch { toast.error('Eroare la deschiderea documentului'); }
  };

  // Filtered cars and routes for search
  const filteredCars = cars.filter(c =>
    !carSearchFilter || (c.name || '').toLowerCase().includes(carSearchFilter.toLowerCase()) ||
    (c.license_plate || '').toLowerCase().includes(carSearchFilter.toLowerCase())
  );
  const filteredRoutes = routes.filter(r =>
    !routeSearchFilter || (r.name || '').toLowerCase().includes(routeSearchFilter.toLowerCase())
  );

  const hasExpiredDocs = carDocuments?.documents?.some(d => d.is_expired);

  if (initialLoading) {
    return <p className="text-center text-slate-500 py-8">Se încarcă...</p>;
  }

  const isFirst5Days = new Date().getDate() <= 5;

  return (
    <>
      {isFirst5Days && (
        <div className="bg-red-700 rounded-xl p-6 mb-3 animate-pulse shadow-xl border-4 border-red-900 text-center" data-testid="fuel-receipt-reminder">
          <Fuel className="w-10 h-10 text-white mx-auto mb-2" />
          <span className="text-2xl font-black text-white uppercase block leading-tight">Vă rog predați bonurile de combustibil!</span>
        </div>
      )}
      {activeTrip ? (
        <Card className="shadow-xl">
          <CardHeader className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-t-lg p-4 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-3">
              <Activity className="w-6 h-6 sm:w-8 sm:h-8" />
              <div>
                <CardTitle className="text-xl sm:text-2xl">Cursă Activă</CardTitle>
                <CardDescription className="text-white/90 text-sm">Închide cursa când ajungi la destinație</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
            <div className="bg-slate-50 p-4 sm:p-6 rounded-lg mb-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="font-semibold text-slate-700">Mașină:</span>
                <span className="text-slate-900 text-right">{activeTrip.car_name} ({activeTrip.car_license})</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-semibold text-slate-700">Ruta:</span>
                <span className="text-slate-900 text-right">{activeTrip.route || activeTrip.route_name || 'N/A'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-semibold text-slate-700">Plecare:</span>
                <span className="text-slate-900 text-right">{activeTrip.departure_location || activeTrip.departure || 'N/A'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-semibold text-slate-700">KM Start:</span>
                <span className="text-slate-900 text-right font-mono">{activeTrip.start_km?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-semibold text-slate-700">Dată:</span>
                <span className="text-slate-900 text-right">{activeTrip.start_time ? new Date(activeTrip.start_time).toLocaleDateString('ro-RO') : activeTrip.date ? new Date(activeTrip.date).toLocaleDateString('ro-RO') : '-'}</span>
              </div>
            </div>

            {/* Car Documents */}
            {carDocuments && (
              <div className="mb-4">
                <button onClick={() => setShowDocuments(!showDocuments)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border text-sm font-medium ${
                    hasExpiredDocs ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'
                  }`}>
                  <span className="flex items-center gap-2">
                    {hasExpiredDocs ? <AlertTriangle className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                    Documente mașină
                  </span>
                  <span>{showDocuments ? '▲' : '▼'}</span>
                </button>
                {showDocuments && carDocuments.documents && (
                  <div className="mt-2 space-y-1">
                    {carDocuments.documents.map((doc, idx) => (
                      <div key={idx}
                        onClick={() => viewDocument(doc)}
                        className={`flex items-center justify-between px-3 py-2 rounded border cursor-pointer text-xs ${
                          doc.is_expired ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'
                        }`}>
                        <span className="font-medium">{doc.label}</span>
                        <span className={doc.is_expired ? 'text-red-600 font-bold' : 'text-green-600'}>
                          {doc.expiry_date ? new Date(doc.expiry_date).toLocaleDateString('ro-RO') : '-'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Fuel Entries */}
            {activeTrip.fuel_entries?.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-semibold text-slate-700 mb-2">Alimentări:</p>
                {activeTrip.fuel_entries.map((fe, idx) => (
                  <div key={idx} className="flex justify-between text-xs bg-amber-50 p-2 rounded border border-amber-100 mb-1">
                    <span>{fe.fuel_type === 'adblue' ? '🔵 AdBlue ' : ''}{fe.liters}L {fe.cost ? `• ${fe.cost} RON` : ''}</span>
                    <span className="text-slate-500">{fe.km_at_fuel ? `@ ${fe.km_at_fuel} km` : ''}</span>
                  </div>
                ))}
              </div>
            )}

            <Button size="sm" variant="outline" onClick={() => setShowAddFuelDialog(true)} className="mb-4 w-full sm:w-auto">
              <Fuel className="w-4 h-4 mr-1" />Adaugă Alimentare
            </Button>

            {/* Close trip form */}
            <div className="space-y-3 border-t pt-4">
              <div>
                <Label htmlFor="endKm">KM Final *</Label>
                <Input id="endKm" type="number" value={endKm} onChange={e => setEndKm(e.target.value)} placeholder="ex: 125000" />
              </div>
              <div>
                <Label htmlFor="closingReason">Motiv închidere</Label>
                <select id="closingReason" value={closingReason} onChange={e => setClosingReason(e.target.value)}
                  className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 bg-white">
                  <option value="">-- Selectează --</option>
                  {closingReasons.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="notes">Observații</Label>
                <Textarea id="notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Opțional" className="h-16" />
              </div>
              <Button onClick={() => closeTrip()} disabled={loading} className="w-full bg-red-600 hover:bg-red-700" data-testid="close-trip-btn">
                <StopCircle className="w-5 h-5 mr-2" />
                {loading ? 'Se procesează...' : 'Închide Cursă'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="shadow-xl">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-t-lg p-4 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-3">
              <Car className="w-6 h-6 sm:w-8 sm:h-8" />
              <div>
                <CardTitle className="text-xl sm:text-2xl">Deschide Cursă Nouă</CardTitle>
                <CardDescription className="text-white/90 text-sm">Selectează mașina și completează datele</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6 space-y-4">
            {/* Car selection */}
            <div>
              <Label>Mașină *</Label>
              {carsError === 'auth' ? (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="cars-auth-error">
                  <p className="font-semibold mb-2">Sesiunea a expirat. Te rog autentifică-te din nou.</p>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => { localStorage.clear(); navigate('/login'); }} data-testid="cars-relogin-btn">
                      Autentificare din nou
                    </Button>
                    <ReportProblemButton auth={auth} section="foaie-parcurs" />
                  </div>
                </div>
              ) : carsError === 'load' ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="cars-load-error">
                  <p className="font-semibold mb-2">Nu s-au putut încărca mașinile. Verifică conexiunea.</p>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => fetchCars()} data-testid="cars-retry-btn">
                      Reîncarcă
                    </Button>
                    <ReportProblemButton auth={auth} section="foaie-parcurs" />
                  </div>
                </div>
              ) : (
                <>
                  <Input placeholder="Caută mașină..." value={carSearchFilter} onChange={e => setCarSearchFilter(e.target.value)} className="mb-2" />
                  <select value={selectedCar} onChange={e => setSelectedCar(e.target.value)}
                    className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 bg-white" data-testid="select-car">
                    <option value="">-- Selectează mașina --</option>
                    {filteredCars.map(c => (
                      <option key={c.id} value={c.id}>{c.name} ({c.license_plate})</option>
                    ))}
                  </select>
                  {cars.length === 0 && (
                    <p className="mt-2 text-xs text-slate-500" data-testid="cars-empty-hint">Nu există mașini disponibile. Contactează administratorul.</p>
                  )}
                </>
              )}
            </div>
            
            {/* Route selection */}
            <div>
              <Label>Ruta *</Label>
              <Input placeholder="Caută rută..." value={routeSearchFilter} onChange={e => setRouteSearchFilter(e.target.value)} className="mb-2" />
              <select value={selectedRoute} onChange={e => setSelectedRoute(e.target.value)}
                className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 bg-white" data-testid="select-route">
                <option value="">-- Selectează ruta --</option>
                {filteredRoutes.map(r => (
                  <option key={r.name || r.id} value={r.name || r.id}>{r.name}</option>
                ))}
              </select>
            </div>

            {/* Departure */}
            <div>
              <Label>Plecare din</Label>
              <Input value={fixedDeparture} onChange={e => setFixedDeparture(e.target.value)} />
            </div>

            {/* Start KM */}
            <div>
              <Label htmlFor="startKm">KM Start *</Label>
              <Input id="startKm" type="number" value={startKm} onChange={e => setStartKm(e.target.value)} placeholder="ex: 124500" data-testid="start-km-input" />
            </div>

            <Button onClick={openTrip} disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="open-trip-btn">
              <PlayCircle className="w-5 h-5 mr-2" />
              {loading ? 'Se procesează...' : 'Deschide Cursă'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Add Fuel Dialog */}
      <Dialog open={showAddFuelDialog} onOpenChange={setShowAddFuelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adaugă Alimentare</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Tip combustibil */}
            <div className="flex gap-2">
              <button type="button" onClick={() => setNewFuelType('motorina')}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold border-2 transition-colors ${
                  newFuelType === 'motorina' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-500'
                }`}>Motorină</button>
              <button type="button" onClick={() => setNewFuelType('adblue')}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold border-2 transition-colors ${
                  newFuelType === 'adblue' ? 'border-sky-500 bg-sky-50 text-sky-700' : 'border-slate-200 text-slate-500'
                }`}>AdBlue</button>
            </div>
            <div>
              <Label>Litri *</Label>
              <Input type="number" step="0.01" value={newFuelLiters} onChange={e => setNewFuelLiters(e.target.value)} placeholder="ex: 45" />
            </div>
            <div>
              <Label>Cost (RON)</Label>
              <Input type="number" step="0.01" value={newFuelCost} onChange={e => setNewFuelCost(e.target.value)} placeholder="ex: 280" />
            </div>
            <div>
              <Label>KM la alimentare</Label>
              <Input type="number" value={newFuelKm} onChange={e => setNewFuelKm(e.target.value)} placeholder="ex: 124800" />
            </div>
            <div>
              <Label>Observații</Label>
              <Textarea value={newFuelNote} onChange={e => setNewFuelNote(e.target.value)} className="h-16" />
            </div>
            <Button onClick={addFuelEntry} disabled={loading} className="w-full">
              {loading ? 'Se procesează...' : 'Adaugă'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fuel Level Dialog - nivel combustibil la început de lună */}
      <Dialog open={showFuelDialog} onOpenChange={setShowFuelDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto p-4">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-base">Nivel Combustibil</DialogTitle>
            <DialogDescription className="text-xs">Selectează nivelul din rezervor</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              {[
                { val: '10', label: 'Gol', pct: '10%', color: 'red' },
                { val: '15', label: 'Sub 1/4', pct: '15%', color: 'orange' },
                { val: '25', label: '1/4', pct: '25%', color: 'orange' },
                { val: '40', label: 'Sub 1/2', pct: '40%', color: 'yellow' },
                { val: '50', label: '1/2', pct: '50%', color: 'yellow' },
                { val: '65', label: 'Peste 1/2', pct: '65%', color: 'green' },
                { val: '75', label: '3/4', pct: '75%', color: 'green' },
                { val: '90', label: 'Aproape', pct: '90%', color: 'blue' },
                { val: '100', label: 'Plin', pct: '100%', color: 'blue' },
              ].map(opt => (
                <button key={opt.val} type="button" onClick={() => setSelectedFuelLevel(opt.val)}
                  className={`p-2 rounded-lg border-2 transition-all text-center ${
                    selectedFuelLevel === opt.val
                      ? `border-${opt.color}-600 bg-${opt.color}-50`
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="text-xs font-medium">{opt.label}</div>
                  <div className="text-[10px] text-slate-500">{opt.pct}</div>
                </button>
              ))}
            </div>
            <div className="flex gap-2 pt-2">
              <Button size="sm" disabled={!selectedFuelLevel} className="flex-1" onClick={async () => {
                const success = await handleSaveFuelLevel();
                if (success) openTrip();
              }}>
                <Fuel className="w-4 h-4 mr-1" />Salvează
              </Button>
              <Button size="sm" variant="outline" onClick={() => {
                setShowFuelDialog(false); setFuelLevelRequired(null); setSelectedFuelLevel('');
              }}>Anulează</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Viewer Dialog */}
      {viewingDocument && (
        <Dialog open={!!viewingDocument} onOpenChange={() => setViewingDocument(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
            <DialogHeader>
              <DialogTitle className="text-base">{viewingDocument?.label}</DialogTitle>
            </DialogHeader>
            <div className="mt-2">
              {viewingDocument.contentType?.includes('pdf') ? (
                <div className="space-y-2">
                  <Button size="sm" variant="outline" onClick={() => {
                    const link = document.createElement('a');
                    link.href = viewingDocument.url;
                    link.download = viewingDocument.document_name || `${viewingDocument.label}.pdf`;
                    link.click();
                  }}>Descarcă PDF</Button>
                  <Button size="sm" variant="outline" className="ml-2" onClick={() => {
                    const win = window.open('', '_blank');
                    if (win) {
                      win.document.write(`<html><head><title>${viewingDocument.label}</title></head><body style="margin:0"><embed src="${viewingDocument.url}" type="application/pdf" width="100%" height="100%"></body></html>`);
                    }
                  }}>Deschide în tab nou</Button>
                </div>
              ) : viewingDocument.contentType?.includes('image') ? (
                <img src={viewingDocument.url} alt={viewingDocument.label} className="max-w-full rounded" />
              ) : (
                <div className="text-center py-4">
                  <Button onClick={() => { const a = document.createElement('a'); a.href = viewingDocument.url; a.download = viewingDocument.document_name || 'document'; a.click(); }}>
                    Descarcă Document
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
